import {
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  useTheme,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemIcon,
  Button,
  TextField
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Person from '@material-ui/icons/Person';
import SupervisorAccount from '@material-ui/icons/SupervisorAccount';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AppStateType } from '~/store/modules/root-reducer';

interface HierarchyUser {
  userRole: string;
  name: string;
  externalCode: string;
  roleExternalCode: string;
  superiorExternalCode: string | null;
  children?: HierarchyUser[];
}

interface HierarchyTreeItemProps {
  user: HierarchyUser;
  selectedUserId: string;
  onUserSelect: (userId: string, userName: string) => void;
  level: number;
  search: string;
}

const filterHierarchy = (users: HierarchyUser[], search: string): HierarchyUser[] => {
  if (!search) return users;

  const term = search.toLowerCase();

  return users
    .map(user => {
      const match = user.name.toLowerCase().includes(term) || user.externalCode.toLowerCase().includes(term);

      const filteredChildren = user.children ? filterHierarchy(user.children, search) : [];

      if (match || filteredChildren.length > 0) {
        return {
          ...user,
          children: filteredChildren
        };
      }

      return null;
    })
    .filter(Boolean) as HierarchyUser[];
};

const HierarchyTreeItem = ({ user, selectedUserId, onUserSelect, level, search }: HierarchyTreeItemProps) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const isVendor = user.userRole === 'vendor' || user.userRole === 'telemarketing';
  const isSelected = selectedUserId === user.externalCode;
  const hasChildren = user.children && user.children.length > 0;

  useEffect(() => {
    if (search && hasChildren) {
      setOpen(true);
    }

    if (!search) {
      setOpen(false);
    }
  }, [search, hasChildren]);

  const handleClick = () => {
    if (isVendor) {
      onUserSelect(user.externalCode, user.name);
    } else if (hasChildren) {
      setOpen(!open);
    }
  };

  const getIcon = () => {
    switch (user.userRole) {
      case 'manager':
      case 'supervisor':
        return <SupervisorAccount />;
      case 'vendor':
      default:
        return <Person />;
    }
  };

  const getRoleColor = () => {
    switch (user.userRole) {
      case 'manager':
        return theme.palette.primary.main;
      case 'supervisor':
        return theme.palette.secondary.main;
      case 'vendor':
        return theme.palette.success.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const roleLabels: Record<string, string> = {
    vendor: 'Vendedor',
    supervisor: 'Supervisor',
    manager: 'Gerente'
  };

  const roleLabel = `${roleLabels[user.userRole] || user.userRole} - ${user.externalCode}`;

  return (
    <>
      <ListItem
        button
        onClick={handleClick}
        style={{
          paddingLeft: theme.spacing(2 + level * 2),
          backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
          cursor: isVendor || hasChildren ? 'pointer' : 'default',
          opacity: isVendor ? 1 : 0.7
        }}
      >
        <ListItemIcon style={{ color: getRoleColor() }}>{getIcon()}</ListItemIcon>

        <ListItemText
          primary={user.name}
          secondary={roleLabel}
          style={{
            color: isVendor ? theme.palette.text.primary : theme.palette.text.secondary
          }}
        />

        {hasChildren && <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>}
      </ListItem>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {user.children?.map(child => (
              <HierarchyTreeItem
                key={child.externalCode}
                user={child}
                selectedUserId={selectedUserId}
                onUserSelect={onUserSelect}
                level={level + 1}
                search={search}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const ChangeContextModal = ({
  showChangeCtxModal,
  setShowChangeCtxModal,
  setCtxSelected,
  onChangeCtx
}: {
  showChangeCtxModal: boolean;
  setShowChangeCtxModal: (show: boolean) => void;
  setCtxSelected: (ctx: string) => void;
  onChangeCtx: (ctx: string) => void;
}) => {
  const { user } = useSelector((state: AppStateType) => state.auth);
  const theme = useTheme();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [search, setSearch] = useState('');

  const handleUserSelect = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setCtxSelected(userId);
  };

  const handleConfirm = () => {
    if (selectedUserId) {
      setCtxSelected(selectedUserId);
      setShowChangeCtxModal(false);
      onChangeCtx(selectedUserId);
    }
  };

  const filteredHierarchy = filterHierarchy(user?.hierarchyTree || [], search);

  return (
    <Dialog
      open={showChangeCtxModal}
      onClose={() => setShowChangeCtxModal(false)}
      PaperComponent={Paper}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <AppBar color="primary" position="static">
          <Toolbar>
            <Box display="flex" justifyContent="center" alignItems="center" width="100%" gridColumnGap={4}>
              <Typography variant="h5">Trocar contexto</Typography>
            </Box>

            <IconButton color="inherit" onClick={() => setShowChangeCtxModal(false)}>
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
      </DialogTitle>

      <DialogContent style={{ padding: `${theme.spacing(0)} ${theme.spacing(3)}px` }}>
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Selecione um vendedor:
          </Typography>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Apenas usuários com perfil de vendedor podem ser selecionados
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar por nome ou código do vendedor"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginTop: theme.spacing(2) }}
          />

          <Paper style={{ maxHeight: '400px', overflow: 'auto', marginTop: theme.spacing(2) }}>
            <List>
              {filteredHierarchy.map(hierarchyUser => (
                <HierarchyTreeItem
                  key={hierarchyUser.externalCode}
                  user={hierarchyUser}
                  selectedUserId={selectedUserId}
                  onUserSelect={handleUserSelect}
                  level={0}
                  search={search}
                />
              ))}
            </List>
          </Paper>

          {selectedUserName && (
            <Box mt={2} p={2} bgcolor="action.selected" borderRadius={1}>
              <Typography variant="body1">
                <strong>Usuário selecionado:</strong> {selectedUserName}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" mt={3} style={{ gap: theme.spacing(2) }}>
            <Button onClick={() => setShowChangeCtxModal(false)}>Cancelar</Button>
            <Button onClick={handleConfirm} disabled={!selectedUserId} color="primary" variant="contained">
              Confirmar
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
